import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Modal } from "@/pages/mentor/MentorSessions";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { ClipboardList, Loader2, Send, Star, CheckCircle2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";

export default function StudentTasks() {
  const { user } = useAuth();
  const female = ["female", "أنثى"].includes(user?.gender);
  const [searchParams] = useSearchParams();
  const focusedTask = searchParams.get("task");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitFor, setSubmitFor] = useState(null);
  const [expandedTask, setExpandedTask] = useState(focusedTask || null);
  const [text, setText] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/tasks");
      setTasks(data.tasks || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!loading && focusedTask) document.getElementById(`task-${focusedTask}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [loading, focusedTask]);

  const submit = async () => {
    if (!text.trim()) { toast.error("أدخل تسليمك"); return; }
    try {
      await api.patch(`/tasks/${submitFor.id}`, { student_submission: text });
      toast.success("تم إرسال التسليم");
      setSubmitFor(null);
      setText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div data-testid="student-tasks">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">مهامي التدريبية</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">مهامي مع الجمعية</h1>
          <p className="mt-2 text-slate-600">{female ? "راجعي المهام المكلّفة بها، سلّمي عملك، واطّلعي على تقييم الجمعية." : "راجع المهام المكلّف بها، سلّم عملك، واطّلع على تقييم الجمعية."}</p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="student-tasks-error" />
        ) : tasks.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="لا توجد مهام حالياً"
            description="ستظهر مهامك التدريبية هنا فور تكليفك من الجمعية."
            testid="student-tasks-empty"
          />
        ) : (
          <div className="space-y-3">
            {tasks.map((t) => (
              <div id={`task-${t.id}`} key={t.id} className={`p-4 bg-white rounded-2xl border card-shadow scroll-mt-6 ${focusedTask === t.id ? "border-teal-500 ring-2 ring-teal-100" : "border-cream-200"}`} data-testid={`stask-${t.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <div className="font-heading font-bold text-slate-900">{t.title}</div>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        t.status === "done" ? "bg-emerald-50 text-emerald-700"
                          : t.status === "submitted" ? "bg-sky-50 text-sky-700"
                          : "bg-amber-50 text-amber-700"
                      }`}>
                        {t.status === "done" ? "مكتملة" : t.status === "submitted" ? "مُرسلة" : "في انتظارك"}
                      </span>
                      {t.score != null && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 arabic-numerals">
                          <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {t.score}/100
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">{t.association_name}{t.due_date ? ` · تاريخ الإنجاز: ${new Date(t.due_date).toLocaleDateString("ar-SA")}` : ""}</div>
                    <button type="button" onClick={() => setExpandedTask(expandedTask === t.id ? null : t.id)} className="mt-2 min-h-11 text-sm font-bold text-teal-700 hover:text-teal-600" aria-expanded={expandedTask === t.id || focusedTask === t.id} data-testid={`task-details-toggle-${t.id}`}>
                      {expandedTask === t.id || focusedTask === t.id ? "إخفاء التفاصيل" : "عرض تفاصيل المهمة"}
                    </button>
                    {(expandedTask === t.id || focusedTask === t.id) && <>
                    {t.description && <div className="text-sm text-slate-700 mt-2">{t.description}</div>}
                    {t.student_submission && (
                      <div className="mt-3 p-3 rounded-lg bg-cream-50 border border-cream-200">
                        <div className="text-xs font-bold text-teal-700 mb-1">تسليمك</div>
                        <div className="text-sm text-slate-700 whitespace-pre-line">{t.student_submission}</div>
                      </div>
                    )}
                    {t.association_feedback && (
                      <div className="mt-2 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                        <div className="text-xs font-bold text-emerald-700 mb-1">تقييم الجمعية</div>
                        <div className="text-sm text-slate-700 whitespace-pre-line">{t.association_feedback}</div>
                      </div>
                    )}
                    {t.mentor_feedback && (
                      <div className="mt-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                        <div className="text-xs font-bold text-indigo-700 mb-1">ملاحظات الموجّه {t.mentor_score != null && <span className="ms-1 arabic-numerals text-amber-700">({t.mentor_score}/100)</span>}</div>
                        <div className="text-sm text-slate-700 whitespace-pre-line">{t.mentor_feedback}</div>
                      </div>
                    )}
                    </>}
                  </div>
                  {t.status === "open" && (
                    <button onClick={() => { setSubmitFor(t); setText(""); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 shrink-0" data-testid={`submit-${t.id}`}>
                      <Send className="h-4 w-4" /> تسليم
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {submitFor && (
          <Modal onClose={() => setSubmitFor(null)} title="تسليم المهمة">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">تسليمك</label>
              <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={female ? "اكتبي ملخص عملك، أو رابطاً لعملك المرفوع..." : "اكتب ملخص عملك، أو رابطاً لعملك المرفوع..."} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3" data-testid="submission-text" />
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setSubmitFor(null)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
              <button onClick={submit} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="submit-btn">
                <CheckCircle2 className="h-4 w-4" /> إرسال
              </button>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}
